import { ProcurementType } from '@/types';
import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithReauth } from '../utils/prepHeaders';
import { ApiType } from '../utils/typeTransformer';

export type args = {
  // Suppliers
  postSuppliers: ApiType<ProcurementType.Suppliers.Post>['args'];
  getAllSuppliers: ApiType<ProcurementType.Suppliers.Get>['args'];
  getSupplier: ApiType<ProcurementType.Suppliers.GetSupplier>['args'];
  getSupplierDetails: ApiType<ProcurementType.Suppliers.GetSupplierDetails>['args'];
  putSupplier: ApiType<ProcurementType.Suppliers.Put>['args'];
  patchSupplier: ApiType<ProcurementType.Suppliers.Patch>['args'];
  deleteSupplier: ApiType<ProcurementType.Suppliers.DeleteSupplier>['args'];
  getSupplierScoreCard: ApiType<ProcurementType.Suppliers.ScoreCard>['args'];
  getSupplierCards: void;

  // Supplier contacts
  postSupplierContacts: ApiType<ProcurementType.SuppliersContacts.Post>['args'];
  getSupplierContact: ApiType<ProcurementType.SuppliersContacts.Get>['args'];
  putSupplierContact: ApiType<ProcurementType.SuppliersContacts.Put>['args'];
  patchSupplierContact: ApiType<ProcurementType.SuppliersContacts.Patch>['args'];
  deleteSupplierContact: ApiType<ProcurementType.SuppliersContacts.Delete>['args'];

  // Draft orders
  postDraftOrders: ApiType<ProcurementType.DraftOrders.Post>['args'];
  getDraftOrder: ApiType<ProcurementType.DraftOrders.GetDraftOrder>['args'];
  putDraftOrder: ApiType<ProcurementType.DraftOrders.Put>['args'];
  patchDraftOrder: ApiType<ProcurementType.DraftOrders.Patch>['args'];

  // Draft orders lines
  postDraftOrderLines: ApiType<ProcurementType.DraftOrderLines.Post>['args'];
  getDraftOrderLine: ApiType<ProcurementType.DraftOrderLines.Get>['args'];
  putDraftOrderLine: ApiType<ProcurementType.DraftOrderLines.Put>['args'];
  patchDraftOrderLine: ApiType<ProcurementType.DraftOrderLines.Patch>['args'];
  deleteDraftOrderLine: ApiType<ProcurementType.DraftOrderLines.Delete>['args'];

  // Purchase orders
  postPurchaseOrder: ApiType<ProcurementType.PurchaseOrders.PostPurchaseOrder>['args'];
  postDirectPurchaseOrder: ApiType<ProcurementType.PurchaseOrders.PostDirectPurchaseOrder>['args'];
  getAllPurchaseOrders: {
    query?: {
      project_id?: number;
      supplier_id?: number;
      status?: string;
      open_only?: boolean;
      cancelled?: boolean;
    };
  };
  getPurchaseOrder: ApiType<ProcurementType.PurchaseOrders.GetPurchaseOrder>['args'];
  putPurchaseOrder: ApiType<ProcurementType.PurchaseOrders.PutPurchaseOrder>['args'];
  patchPurchaseOrder: ApiType<ProcurementType.PurchaseOrders.PatchPurchaseOrder>['args'];
  postPurchaseOrderCancel: ApiType<ProcurementType.PurchaseOrders.PostCancel>['args'];
  postPurchaseOrderRestore: ApiType<ProcurementType.PurchaseOrders.PostRestore>['args'];
  deletePurchaseOrder: ApiType<ProcurementType.PurchaseOrders.DeletePurchaseOrder>['args'];
  postPurchaseOrderStatus: ApiType<ProcurementType.PurchaseOrders.PostStatus>['args'];
  postPurchaseOrderReceive: ApiType<ProcurementType.PurchaseOrders.PostReceive>['args'];
  deletePurchaseOrderReceive: ApiType<ProcurementType.PurchaseOrders.DeleteReceive>['args'];
  getPurchaseOrderExpectedReceipts: ApiType<ProcurementType.PurchaseOrders.GetExpectedReceipts>['args'];
  exportPurchaseOrderPdf: ApiType<ProcurementType.PurchaseOrders.ExportPdf>['args'];
  exportPurchaseOrderLog: ApiType<ProcurementType.PurchaseOrders.ExportLog>['args'];
  getPurchaseOrdersByPart: ApiType<ProcurementType.PurchaseOrders.GetPurchaseOrdersByPart>['args'];
  postImportPreview: ApiType<ProcurementType.PurchaseOrders.PostImportPreview>['args'];
  postImportCommit: ApiType<ProcurementType.PurchaseOrders.PostImportCommit>['args'];

  // Purchase order invoices
  postPurchaseOrderInvoice: ApiType<ProcurementType.PurchaseOrders.PostPurchaseOrderInvoice>['args'];
  getPurchaseOrderInvoicesAll: ApiType<ProcurementType.PurchaseOrders.GetPurchaseOrderInvoicesAll>['args'];
  getPurchaseOrderInvoice: ApiType<ProcurementType.PurchaseOrders.GetPurchaseOrderInvoice>['args'];
  patchPurchaseOrderInvoice: ApiType<ProcurementType.PurchaseOrders.PatchPurchaseOrderInvoice>['args'];
  deletePurchaseOrderInvoice: ApiType<ProcurementType.PurchaseOrders.DeletePurchaseOrderInvoice>['args'];

  // Purchase order lines
  getPurchaseOrderLine: ApiType<ProcurementType.PurchaseOrderLines.Get>['args'];
  putPurchaseOrderLine: ApiType<ProcurementType.PurchaseOrderLines.Put>['args'];
  patchPurchaseOrderLine: ApiType<ProcurementType.PurchaseOrderLines.Patch>['args'];
  postPurchaseOrderLines: ApiType<ProcurementType.PurchaseOrderLines.Post>['args'];
  deletePurchaseOrderLine: ApiType<ProcurementType.PurchaseOrderLines.Delete>['args'];

  //Dashboard
  getProcurementDashboard: {
    query: {
      project: number;
      window_days?: number;
    };
  };
};
export type responses = {
  // Suppliers
  postSuppliers: ApiType<ProcurementType.Suppliers.Post>['response'];
  getAllSuppliers: ApiType<ProcurementType.Suppliers.Get>['response'];
  getSupplier: ApiType<ProcurementType.Suppliers.GetSupplier>['response'];
  getSupplierDetails: ApiType<ProcurementType.Suppliers.GetSupplierDetails>['response'];
  putSupplier: ApiType<ProcurementType.Suppliers.Put>['response'];
  patchSupplier: ApiType<ProcurementType.Suppliers.Patch>['response'];
  deleteSupplier: void;
  getSupplierScoreCard: ApiType<ProcurementType.Suppliers.ScoreCard>['response'];
  getSupplierCards: ApiType<ProcurementType.Suppliers.Cards>['response'];

  // Supplier contacts
  postSupplierContacts: ApiType<ProcurementType.SuppliersContacts.Post>['response'];
  getSupplierContact: ApiType<ProcurementType.SuppliersContacts.Get>['response'];
  putSupplierContact: ApiType<ProcurementType.SuppliersContacts.Put>['response'];
  patchSupplierContact: ApiType<ProcurementType.SuppliersContacts.Patch>['response'];

  // Draft orders
  postDraftOrders: ApiType<ProcurementType.DraftOrders.Post>['response'];
  getAllDraftOrders: ApiType<ProcurementType.DraftOrders.Get>['response'];
  getDraftOrder: ApiType<ProcurementType.DraftOrders.GetDraftOrder>['response'];
  putDraftOrder: ApiType<ProcurementType.DraftOrders.Put>['response'];
  patchDraftOrder: ApiType<ProcurementType.DraftOrders.Patch>['response'];

  // Draft orders lines
  postDraftOrderLines: ApiType<ProcurementType.DraftOrderLines.Post>['response'];
  getDraftOrderLine: ApiType<ProcurementType.DraftOrderLines.Get>['response'];
  putDraftOrderLine: ApiType<ProcurementType.DraftOrderLines.Put>['response'];
  patchDraftOrderLine: ApiType<ProcurementType.DraftOrderLines.Patch>['response'];

  // Purchase orders
  postPurchaseOrder: ApiType<ProcurementType.PurchaseOrders.PostPurchaseOrder>['response'];
  postDirectPurchaseOrder: ApiType<ProcurementType.PurchaseOrders.PostDirectPurchaseOrder>['response'];
  getAllPurchaseOrders: ApiType<ProcurementType.PurchaseOrders.GetAllPurchaseOrders>['response'];
  getPurchaseOrder: ApiType<ProcurementType.PurchaseOrders.GetPurchaseOrder>['response'];
  putPurchaseOrder: ApiType<ProcurementType.PurchaseOrders.PutPurchaseOrder>['response'];
  patchPurchaseOrder: ApiType<ProcurementType.PurchaseOrders.PatchPurchaseOrder>['response'];
  postPurchaseOrderCancel: ApiType<ProcurementType.PurchaseOrders.PostCancel>['response'];
  postPurchaseOrderRestore: ApiType<ProcurementType.PurchaseOrders.PostRestore>['response'];
  deletePurchaseOrder: void;
  postPurchaseOrderStatus: ApiType<ProcurementType.PurchaseOrders.PostStatus>['response'];
  postPurchaseOrderReceive: ApiType<ProcurementType.PurchaseOrders.PostReceive>['response'];
  deletePurchaseOrderReceive: ApiType<ProcurementType.PurchaseOrders.DeleteReceive>['response'];
  exportPurchaseOrderPdf: ApiType<ProcurementType.PurchaseOrders.ExportPdf>['response'];
  exportPurchaseOrderLog: ApiType<ProcurementType.PurchaseOrders.ExportLog>['response'];
  getPurchaseOrdersByPart: ApiType<ProcurementType.PurchaseOrders.GetPurchaseOrdersByPart>['response'];
  postImportPreview: ApiType<ProcurementType.PurchaseOrders.PostImportPreview>['response'];
  postImportCommit: ApiType<ProcurementType.PurchaseOrders.PostImportCommit>['response'];

  // Purchase order invoices
  postPurchaseOrderInvoice: ApiType<ProcurementType.PurchaseOrders.PostPurchaseOrderInvoice>['response'];
  getPurchaseOrderInvoicesAll: ApiType<ProcurementType.PurchaseOrders.GetPurchaseOrderInvoicesAll>['response'];
  getPurchaseOrderInvoice: ApiType<ProcurementType.PurchaseOrders.GetPurchaseOrderInvoice>['response'];
  patchPurchaseOrderInvoice: ApiType<ProcurementType.PurchaseOrders.PatchPurchaseOrderInvoice>['response'];
  deletePurchaseOrderInvoice: void;

  // Purchase order lines
  getPurchaseOrderLine: ApiType<ProcurementType.PurchaseOrderLines.Get>['response'];
  putPurchaseOrderLine: ApiType<ProcurementType.PurchaseOrderLines.Put>['response'];
  patchPurchaseOrderLine: ApiType<ProcurementType.PurchaseOrderLines.Patch>['response'];
  postPurchaseOrderLines: ApiType<ProcurementType.PurchaseOrderLines.Post>['response'];
  deletePurchaseOrderLine: void;

  //Dashboard
  getProcurementDashboard: any;
};
const keepUnusedDataFor = 86400;

export const procurementApi = createApi({
  reducerPath: 'procurementApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['Procurement'],

  endpoints: (builder) => ({
    postSuppliers: builder.mutation<
      responses['postSuppliers'],
      args['postSuppliers']
    >({
      query: ({ body }) => ({
        url: '/procurement/suppliers/',
        method: 'POST',
        body,
        validateStatus: (response, result) =>
          response.status === 201 && !result.isError,
      }),
      invalidatesTags: (_result, _error, _arg) =>
        _result
          ? [
              { type: 'Procurement', id: 'suppliers' },
              { type: 'Procurement', id: 'supplier-cards' },
            ]
          : [],
    }),

    getAllSuppliers: builder.query<
      responses['getAllSuppliers'],
      args['getAllSuppliers']
    >({
      query: ({ query }) => ({
        url: '/procurement/suppliers/',
        method: 'GET',
        params: query,
        validateStatus: (response, result) =>
          response.status === 200 && !result.isError,
      }),
      keepUnusedDataFor,
      providesTags: (result) =>
        result ? [{ type: 'Procurement', id: 'suppliers' }] : [],
    }),

    patchSupplier: builder.mutation<
      responses['patchSupplier'],
      args['patchSupplier']
    >({
      query: ({ body, params }) => ({
        url: `/procurement/suppliers/${params.id}/`,
        method: 'PATCH',
        body,
        validateStatus: (response, result) =>
          response.status === 200 && !result.isError,
      }),
      invalidatesTags: (_result, _error, _arg) =>
        _result
          ? [
              { type: 'Procurement', id: 'suppliers' },
              { type: 'Procurement', id: 'supplier-cards' },
            ]
          : [],
    }),

    putSupplier: builder.mutation<
      responses['putSupplier'],
      args['putSupplier']
    >({
      query: ({ body, params }) => ({
        url: `/procurement/suppliers/${params.id}/`,
        method: 'PUT',
        body,
        validateStatus: (response, result) =>
          response.status === 200 && !result.isError,
      }),
      invalidatesTags: (_result, _error, _arg) =>
        _result
          ? [
              { type: 'Procurement', id: 'suppliers' },
              { type: 'Procurement', id: 'supplier-cards' },
            ]
          : [],
    }),

    deleteSupplier: builder.mutation<
      responses['deleteSupplier'],
      args['deleteSupplier']
    >({
      query: ({ params }) => ({
        url: `/procurement/suppliers/${params.id}/`,
        method: 'DELETE',
        validateStatus: (response) => response.status === 204,
      }),
      invalidatesTags: (_result, _error, _arg) => [
        { type: 'Procurement', id: 'suppliers' },
        { type: 'Procurement', id: 'supplier-cards' },
        { type: 'Procurement', id: `supplier-${_arg.params.id}` },
        { type: 'Procurement', id: 'supplier-details' },
      ],
    }),

    getSupplier: builder.query<responses['getSupplier'], args['getSupplier']>({
      query: ({ params }) => ({
        url: `/procurement/suppliers/${params.id}/`,
        method: 'GET',
        validateStatus: (response, result) =>
          response.status === 200 && !result.isError,
      }),
      keepUnusedDataFor,
      providesTags: (_result, _error, _arg) =>
        _result
          ? [
              {
                type: 'Procurement',
                id: 'suppliers',
              },
              { type: 'Procurement', id: `supplier-${_arg.params.id}` },
            ]
          : [],
    }),

    getSupplierScoreCard: builder.query<
      responses['getSupplierScoreCard'],
      args['getSupplierScoreCard']
    >({
      query: ({ params }) => ({
        url: `/procurement/suppliers/${params.supplier_id}/scorecard/`,
        method: 'GET',
        validateStatus: (response, result) =>
          response.status === 200 && !result.isError,
      }),
      keepUnusedDataFor,
      providesTags: (_result, _error, _arg) =>
        _result
          ? [
              {
                type: 'Procurement',
                id: 'suppliers',
              },
              {
                type: 'Procurement',
                id: `supplier-scorecard-${_arg.params.supplier_id}`,
              },
            ]
          : [],
    }),

    // Supplier contacts
    postSupplierContacts: builder.mutation<
      responses['postSupplierContacts'],
      args['postSupplierContacts']
    >({
      query: ({ body, params }) => ({
        url: `/procurement/suppliers/${params.supplier_id}/contacts/`,
        method: 'POST',
        body,
        validateStatus: (response, result) =>
          response.status === 201 && !result.isError,
      }),
      invalidatesTags: (_result, _error, _arg) =>
        _result ? [{ type: 'Procurement', id: 'suppliers-contacts' }] : [],
    }),

    getSupplierCards: builder.query<responses['getSupplierCards'], void>({
      query: () => ({
        url: '/procurement/suppliers/cards/',
        method: 'GET',
        validateStatus: (response, result) =>
          response.status === 200 && !result.isError,
      }),
      keepUnusedDataFor,
      providesTags: (_result) =>
        _result ? [{ type: 'Procurement', id: 'supplier-cards' }] : [],
    }),

    getSupplierContact: builder.query<
      responses['getSupplierContact'],
      args['getSupplierContact']
    >({
      query: ({ params }) => ({
        url: `/procurement/supplier-contacts/${params.supplier_id}/`,
        method: 'GET',
        validateStatus: (response, result) =>
          response.status === 200 && !result.isError,
      }),
      keepUnusedDataFor,
      providesTags: (_result, _error, _arg) =>
        _result
          ? [
              {
                type: 'Procurement',
                id: `suppliers-contact-${_arg.params.supplier_id}`,
              },
            ]
          : [],
    }),

    putSupplierContact: builder.mutation<
      responses['putSupplierContact'],
      args['putSupplierContact']
    >({
      query: ({ body, params }) => ({
        url: `/procurement/supplier-contacts/${params.id}/`,
        method: 'PUT',
        body,
        validateStatus: (response, result) =>
          response.status === 200 && !result.isError,
      }),
      invalidatesTags: (_result, _error, _arg) =>
        _result ? [{ type: 'Procurement', id: 'suppliers-contacts' }] : [],
    }),

    patchSupplierContact: builder.mutation<
      responses['patchSupplierContact'],
      args['patchSupplierContact']
    >({
      query: ({ body, params }) => ({
        url: `/procurement/supplier-contacts/${params.id}/`,
        method: 'PATCH',
        body,
        validateStatus: (response, result) =>
          response.status === 200 && !result.isError,
      }),
      invalidatesTags: (_result, _error, _arg) =>
        _result ? [{ type: 'Procurement', id: 'suppliers-contacts' }] : [],
    }),

    deleteSupplierContact: builder.mutation<
      void,
      args['deleteSupplierContact']
    >({
      query: ({ params }) => ({
        url: `/procurement/supplier-contacts/${params.id}/`,
        method: 'DELETE',
        validateStatus: (response) => response.status === 204,
      }),
      invalidatesTags: (_result, _error, _arg) => [
        { type: 'Procurement', id: 'suppliers-contacts' },
      ],
    }),

    //Draft orders
    postDraftOrders: builder.mutation<
      responses['postDraftOrders'],
      args['postDraftOrders']
    >({
      query: ({ body }) => ({
        url: '/procurement/draft-orders/',
        method: 'POST',
        body,
        validateStatus: (response, result) =>
          response.status === 201 && !result.isError,
      }),
      invalidatesTags: (_result, _error, _arg) =>
        _result ? [{ type: 'Procurement', id: 'draft-orders' }] : [],
    }),

    getAllDraftOrders: builder.query<responses['getAllDraftOrders'], void>({
      query: () => ({
        url: '/procurement/draft-orders/',
        method: 'GET',
        validateStatus: (response, result) =>
          response.status === 200 && !result.isError,
      }),
      keepUnusedDataFor,
      providesTags: (_result, _error, _arg) =>
        _result ? [{ type: 'Procurement', id: 'draft-orders' }] : [],
    }),

    getDraftOrder: builder.query<
      responses['getDraftOrder'],
      args['getDraftOrder']
    >({
      query: ({ params }) => ({
        url: `/procurement/draft-orders/${params.id}/`,
        method: 'GET',
        validateStatus: (response, result) =>
          response.status === 200 && !result.isError,
      }),
      keepUnusedDataFor,
      providesTags: (_result, _error, _arg) =>
        _result
          ? [
              { type: 'Procurement', id: 'draft-orders' },
              { type: 'Procurement', id: `draft-order-${_arg.params.id}` },
            ]
          : [],
    }),

    patchDraftOrder: builder.mutation<
      responses['patchDraftOrder'],
      args['patchDraftOrder']
    >({
      query: ({ body, params }) => ({
        url: `/procurement/draft-orders/${params.id}/`,
        method: 'PATCH',
        body,
        validateStatus: (response, result) =>
          response.status === 200 && !result.isError,
      }),
      invalidatesTags: (_result, _error, _arg) =>
        _result ? [{ type: 'Procurement', id: 'draft-orders' }] : [],
    }),

    putDraftOrder: builder.mutation<
      responses['putDraftOrder'],
      args['putDraftOrder']
    >({
      query: ({ body, params }) => ({
        url: `/procurement/draft-orders/${params.id}/`,
        method: 'PUT',
        body,
        validateStatus: (response, result) =>
          response.status === 200 && !result.isError,
      }),
      invalidatesTags: (_result, _error, _arg) =>
        _result ? [{ type: 'Procurement', id: 'draft-orders' }] : [],
    }),

    // Draft orders lines
    postDraftOrderLines: builder.mutation<
      responses['postDraftOrderLines'],
      args['postDraftOrderLines']
    >({
      query: ({ body, params }) => ({
        url: `/procurement/draft-orders/${params.draft_id}/lines/`,
        method: 'POST',
        body,
        validateStatus: (response, result) =>
          response.status === 200 && !result.isError,
      }),
      invalidatesTags: (_result, _error, _arg) =>
        _result ? [{ type: 'Procurement', id: 'draft-orders' }] : [],
    }),

    getDraftOrderLine: builder.query<
      responses['getDraftOrderLine'],
      args['getDraftOrderLine']
    >({
      query: ({ params }) => ({
        url: `/procurement/draft-order-lines/${params.id}/`,
        method: 'GET',
        validateStatus: (response, result) =>
          response.status === 200 && !result.isError,
      }),
      keepUnusedDataFor,
      providesTags: (_result, _error, _arg) =>
        _result
          ? [
              { type: 'Procurement', id: 'draft-orders' },
              {
                type: 'Procurement',
                id: `draft-orders-line-${_arg.params.id}`,
              },
            ]
          : [],
    }),

    patchDraftOrderLine: builder.mutation<
      responses['patchDraftOrderLine'],
      args['patchDraftOrderLine']
    >({
      query: ({ body, params }) => ({
        url: `/procurement/draft-order-lines/${params.id}/`,
        method: 'PATCH',
        body,
        validateStatus: (response, result) =>
          response.status === 200 && !result.isError,
      }),
      invalidatesTags: (_result, _error, _arg) =>
        _result ? [{ type: 'Procurement', id: 'draft-orders' }] : [],
    }),

    putDraftOrderLine: builder.mutation<
      responses['putDraftOrderLine'],
      args['putDraftOrderLine']
    >({
      query: ({ body, params }) => ({
        url: `/procurement/draft-order-lines/${params.id}/`,
        method: 'PUT',
        body,
        validateStatus: (response, result) =>
          response.status === 200 && !result.isError,
      }),
      invalidatesTags: (_result, _error, _arg) =>
        _result ? [{ type: 'Procurement', id: 'draft-orders' }] : [],
    }),

    deleteDraftOrderLine: builder.mutation<void, args['deleteDraftOrderLine']>({
      query: ({ params }) => ({
        url: `/procurement/draft-order-lines/${params.id}/`,
        method: 'DELETE',
        validateStatus: (response, _result) => response.status === 204,
      }),
      invalidatesTags: (_result, _error, _arg) => [
        { type: 'Procurement', id: 'draft-orders' },
      ],
    }),

    // Purchase orders
    postDirectPurchaseOrder: builder.mutation<
      responses['postDirectPurchaseOrder'],
      args['postDirectPurchaseOrder']
    >({
      query: ({ body }) => ({
        url: '/procurement/purchase-orders/',
        method: 'POST',
        body,
        validateStatus: (response, result) =>
          response.status === 201 && !result.isError,
      }),
      invalidatesTags: (_result) =>
        _result
          ? [
              { type: 'Procurement', id: 'purchase-orders' },
              { type: 'Procurement', id: 'procurement-dashboard' },
              { type: 'Procurement', id: 'supplier-cards' },
              { type: 'Procurement', id: 'supplier-details' },
            ]
          : [],
    }),

    postPurchaseOrder: builder.mutation<
      responses['postPurchaseOrder'],
      args['postPurchaseOrder']
    >({
      query: ({ params, body }) => ({
        url: `/procurement/draft-orders/${params.draft_id}/convert-to-po/`,
        method: 'POST',
        body,
        validateStatus: (response, result) =>
          response.status === 201 && !result.isError,
      }),
      invalidatesTags: (_result, _error, _arg) =>
        _result
          ? [
              { type: 'Procurement', id: 'draft-orders' },
              { type: 'Procurement', id: 'purchase-orders' },
              { type: 'Procurement', id: 'procurement-dashboard' },
              { type: 'Procurement', id: 'supplier-cards' },
              { type: 'Procurement', id: 'supplier-details' },
            ]
          : [],
    }),

    getAllPurchaseOrders: builder.query<
      responses['getAllPurchaseOrders'],
      args['getAllPurchaseOrders']
    >({
      query: (arg) => ({
        url: '/procurement/purchase-orders/',
        method: 'GET',
        params: arg?.query,
        validateStatus: (response, result) =>
          response.status === 200 && !result.isError,
      }),
      keepUnusedDataFor,
      providesTags: (_result, _error, _arg) =>
        _result ? [{ type: 'Procurement', id: 'purchase-orders' }] : [],
    }),

    getPurchaseOrder: builder.query<
      responses['getPurchaseOrder'],
      args['getPurchaseOrder']
    >({
      query: ({ params }) => ({
        url: `/procurement/purchase-orders/${params.id}/`,
        method: 'GET',
        validateStatus: (response, result) =>
          response.status === 200 && !result.isError,
      }),
      providesTags: (_result, _error, _arg) =>
        _result
          ? [
              { type: 'Procurement', id: 'purchase-orders' },
              {
                type: 'Procurement',
                id: `purchase-order-${_arg.params.id}`,
              },
            ]
          : [],
    }),

    patchPurchaseOrder: builder.mutation<
      responses['patchPurchaseOrder'],
      args['patchPurchaseOrder']
    >({
      query: ({ body, params }) => ({
        url: `/procurement/purchase-orders/${params.id}/`,
        method: 'PATCH',
        body,
        validateStatus: (response, result) =>
          response.status === 200 && !result.isError,
      }),
      invalidatesTags: (_result, _error, _arg) =>
        _result
          ? [
              { type: 'Procurement', id: 'purchase-orders' },
              // Refresh the cached detail view so a renamed po_number
              // (or any other field) shows up immediately.
              {
                type: 'Procurement',
                id: `purchase-order-${_arg.params.id}`,
              },
              // The per-PO expected-receipts list embeds po_number as
              // `reference`; refresh it after a rename.
              {
                type: 'Procurement',
                id: `purchase-order-expected-receipts-${_arg.params.id}`,
              },
              { type: 'Procurement', id: 'procurement-dashboard' },
              { type: 'Procurement', id: 'supplier-cards' },
              { type: 'Procurement', id: 'supplier-details' },
            ]
          : [],
    }),

    putPurchaseOrder: builder.mutation<
      responses['putPurchaseOrder'],
      args['putPurchaseOrder']
    >({
      query: ({ body, params }) => ({
        url: `/procurement/purchase-orders/${params.id}/`,
        method: 'PUT',
        body,
        validateStatus: (response, result) =>
          response.status === 200 && !result.isError,
      }),
      invalidatesTags: (_result, _error, _arg) =>
        _result ? [{ type: 'Procurement', id: 'purchase-orders' }] : [],
    }),

    getPurchaseOrdersByPart: builder.query<
      responses['getPurchaseOrdersByPart'],
      args['getPurchaseOrdersByPart']
    >({
      query: ({ query }) => ({
        url: '/procurement/purchase-orders/search-by-part/',
        method: 'GET',
        params: query,
        validateStatus: (response, result) =>
          response.status === 200 && !result.isError,
      }),
      providesTags: (_result, _error, _arg) =>
        _result
          ? [
              { type: 'Procurement', id: 'purchase-orders' },
              { type: 'Procurement', id: `purchase-order-query-${_arg.query}` },
            ]
          : [],
    }),

    exportPurchaseOrderPdf: builder.mutation<
      responses['exportPurchaseOrderPdf'],
      args['exportPurchaseOrderPdf']
    >({
      query: ({ params, body }) => ({
        url: `/procurement/purchase-orders/${params.po_id}/export-pdf/`,
        method: 'POST',
        body,
        // CRITICAL: tell fetchBaseQuery to return a Blob
        responseHandler: async (response) => await response.blob(),
        validateStatus: (response) => response.status === 200,
      }),
    }),

    exportPurchaseOrderLog: builder.mutation<
      responses['exportPurchaseOrderLog'],
      args['exportPurchaseOrderLog']
    >({
      query: ({ body }) => ({
        url: '/procurement/purchase-orders/export-log/',
        method: 'POST',
        body,
        responseHandler: async (response) => await response.blob(),
        validateStatus: (response) => response.status === 200,
      }),
    }),

    postPurchaseOrderCancel: builder.mutation<
      responses['postPurchaseOrderCancel'],
      args['postPurchaseOrderCancel']
    >({
      query: ({ params }) => ({
        url: `/procurement/purchase-orders/${params.po_id}/cancel/`,
        method: 'POST',
        validateStatus: (response, result) =>
          response.status === 200 && !result.isError,
      }),
      invalidatesTags: (_result, _error, _arg) =>
        _result
          ? [
              { type: 'Procurement', id: 'purchase-orders' },
              {
                type: 'Procurement',
                id: `purchase-order-${_arg.params.po_id}`,
              },
              { type: 'Procurement', id: 'procurement-dashboard' },
              { type: 'Procurement', id: 'supplier-cards' },
              { type: 'Procurement', id: 'supplier-details' },
            ]
          : [],
    }),

    postPurchaseOrderRestore: builder.mutation<
      responses['postPurchaseOrderRestore'],
      args['postPurchaseOrderRestore']
    >({
      query: ({ params }) => ({
        url: `/procurement/purchase-orders/${params.po_id}/restore/`,
        method: 'POST',
        validateStatus: (response, result) =>
          response.status === 200 && !result.isError,
      }),
      invalidatesTags: (_result, _error, _arg) =>
        _result
          ? [
              { type: 'Procurement', id: 'purchase-orders' },
              {
                type: 'Procurement',
                id: `purchase-order-${_arg.params.po_id}`,
              },
              { type: 'Procurement', id: 'procurement-dashboard' },
              { type: 'Procurement', id: 'supplier-cards' },
              { type: 'Procurement', id: 'supplier-details' },
            ]
          : [],
    }),

    deletePurchaseOrder: builder.mutation<
      responses['deletePurchaseOrder'],
      args['deletePurchaseOrder']
    >({
      query: ({ params }) => ({
        url: `/procurement/purchase-orders/${params.id}/`,
        method: 'DELETE',
        validateStatus: (response) => response.status === 204,
      }),
      invalidatesTags: (_result, _error, _arg) => [
        { type: 'Procurement', id: 'purchase-orders' },
        { type: 'Procurement', id: `purchase-order-${_arg.params.id}` },
        { type: 'Procurement', id: 'procurement-dashboard' },
        { type: 'Procurement', id: 'supplier-cards' },
        { type: 'Procurement', id: 'supplier-details' },
        { type: 'Procurement', id: 'draft-orders' },
      ],
    }),

    postPurchaseOrderStatus: builder.mutation<
      responses['postPurchaseOrderStatus'],
      args['postPurchaseOrderStatus']
    >({
      query: ({ params, body }) => ({
        url: `/procurement/purchase-orders/${params.po_id}/status/`,
        method: 'POST',
        body,
        validateStatus: (response, result) =>
          response.status === 200 && !result.isError,
      }),
      invalidatesTags: (_result, _error, _arg) =>
        _result ? [{ type: 'Procurement', id: 'purchase-orders' }] : [],
    }),

    postPurchaseOrderReceive: builder.mutation<
      responses['postPurchaseOrderReceive'],
      args['postPurchaseOrderReceive']
    >({
      query: ({ params, body }) => ({
        url: `/procurement/purchase-orders/${params.po_id}/receive/`,
        method: 'POST',
        body,
        validateStatus: (response, result) =>
          response.status === 200 && !result.isError,
      }),
      invalidatesTags: (_result, _error, _arg) =>
        _result
          ? [
              { type: 'Procurement', id: 'purchase-orders' },
              {
                type: 'Procurement',
                id: `purchase-order-${_arg.params.po_id}`,
              },
              { type: 'Procurement', id: 'procurement-dashboard' },
              { type: 'Procurement', id: 'supplier-cards' },
            ]
          : [],
    }),

    deletePurchaseOrderReceive: builder.mutation<
      responses['deletePurchaseOrderReceive'],
      args['deletePurchaseOrderReceive']
    >({
      query: ({ params }) => ({
        url: `/procurement/purchase-orders/${params.po_id}/receive/`,
        method: 'DELETE',
        validateStatus: (response, result) =>
          response.status === 200 && !result.isError,
      }),
      invalidatesTags: (_result, _error, _arg) =>
        _result
          ? [
              { type: 'Procurement', id: 'purchase-orders' },
              {
                type: 'Procurement',
                id: `purchase-order-${_arg.params.po_id}`,
              },
              { type: 'Procurement', id: 'procurement-dashboard' },
              { type: 'Procurement', id: 'supplier-cards' },
            ]
          : [],
    }),

    postImportPreview: builder.mutation<
      responses['postImportPreview'],
      args['postImportPreview']
    >({
      query: ({ body }) => ({
        url: '/procurement/purchase-orders/import/preview/',
        method: 'POST',
        body,
        validateStatus: (response, result) =>
          response.status === 200 && !result.isError,
      }),
    }),

    postImportCommit: builder.mutation<
      responses['postImportCommit'],
      args['postImportCommit']
    >({
      query: ({ body }) => ({
        url: '/procurement/purchase-orders/import/commit/',
        method: 'POST',
        body,
        validateStatus: (response, result) =>
          response.status === 201 && !result.isError,
      }),
      invalidatesTags: (_result) =>
        _result
          ? [
              { type: 'Procurement', id: 'purchase-orders' },
              { type: 'Procurement', id: 'procurement-dashboard' },
              { type: 'Procurement', id: 'supplier-cards' },
              { type: 'Procurement', id: 'supplier-details' },
            ]
          : [],
    }),

    getPurchaseOrderExpectedReceipts: builder.query<
      void,
      args['getPurchaseOrderExpectedReceipts']
    >({
      query: ({ params }) => ({
        url: `/procurement/purchase-orders/${params.po_id}/expected-receipts/`,
        method: 'GET',
        validateStatus: (response, result) =>
          response.status === 200 && !result.isError,
      }),
      keepUnusedDataFor,
      providesTags: (_result, _error, _arg) =>
        _result
          ? [
              {
                type: 'Procurement',
                id: `purchase-order-expected-receipts-${_arg.params.po_id}`,
              },
            ]
          : [],
    }),

    // Purchase order invoices
    postPurchaseOrderInvoice: builder.mutation<
      responses['postPurchaseOrderInvoice'],
      args['postPurchaseOrderInvoice']
    >({
      query: ({ body, params }) => ({
        url: `/procurement/purchase-orders/${params.po_id}/invoices/`,
        method: 'POST',
        body,
        validateStatus: (response, result) =>
          response.status === 201 && !result.isError,
      }),
      invalidatesTags: (_result, _error, _arg) =>
        _result ? [{ type: 'Procurement', id: 'purchase-order-invoices' }] : [],
    }),

    getPurchaseOrderInvoicesAll: builder.query<
      responses['getPurchaseOrderInvoicesAll'],
      args['getPurchaseOrderInvoicesAll']
    >({
      query: ({ params }) => ({
        url: `/procurement/purchase-orders/${params.po_id}/invoices/`,
        method: 'GET',
        validateStatus: (response, result) =>
          response.status === 200 && !result.isError,
      }),
      providesTags: (_result, _error, _arg) =>
        _result
          ? [
              { type: 'Procurement', id: 'purchase-order-invoices' },
              {
                type: 'Procurement',
                id: `purchase-order-invoices-${_arg.params.po_id}`,
              },
            ]
          : [],
    }),

    getPurchaseOrderInvoice: builder.query<
      responses['getPurchaseOrderInvoice'],
      args['getPurchaseOrderInvoice']
    >({
      query: ({ params }) => ({
        url: `/procurement/purchase-orders/${params.po_id}/invoices/${params.invoice_id}/`,
        method: 'GET',
        validateStatus: (response, result) =>
          response.status === 200 && !result.isError,
      }),
      keepUnusedDataFor,
      providesTags: (_result, _error, _arg) =>
        _result
          ? [
              { type: 'Procurement', id: 'purchase-order-invoices' },
              {
                type: 'Procurement',
                id: `purchase-order-invoice-${_arg.params.invoice_id}`,
              },
            ]
          : [],
    }),

    getProcurementDashboard: builder.query<
      responses['getProcurementDashboard'],
      args['getProcurementDashboard']
    >({
      query: ({ query }) => ({
        url: '/procurement/dashboard/',
        method: 'GET',
        params: query,
        validateStatus: (response, result) =>
          response.status === 200 && !result.isError,
      }),
      keepUnusedDataFor,
      providesTags: (_result, _error, _arg) =>
        _result
          ? [
              { type: 'Procurement', id: 'procurement-dashboard' },
              {
                type: 'Procurement',
                id: `procurement-dashboard-${_arg.query.project}`,
              },
            ]
          : [],
    }),

    patchPurchaseOrderInvoice: builder.mutation<
      responses['patchPurchaseOrderInvoice'],
      args['patchPurchaseOrderInvoice']
    >({
      query: ({ body, params }) => ({
        url: `/procurement/purchase-orders/${params.po_id}/invoices/${params.invoice_id}/`,
        method: 'PATCH',
        body,
        validateStatus: (response, result) =>
          response.status === 200 && !result.isError,
      }),
      invalidatesTags: (_result, _error, _arg) =>
        _result ? [{ type: 'Procurement', id: 'purchase-order-invoices' }] : [],
    }),

    deletePurchaseOrderInvoice: builder.mutation<
      responses['deletePurchaseOrderInvoice'],
      args['deletePurchaseOrderInvoice']
    >({
      query: ({ params }) => ({
        url: `/procurement/purchase-orders/${params.po_id}/invoices/${params.invoice_id}/`,
        method: 'DELETE',
        validateStatus: (response) => response.status === 204,
      }),
      invalidatesTags: (_result, _error, _arg) => [
        { type: 'Procurement', id: 'purchase-order-invoices' },
      ],
    }),

    // Purchase order lines
    getPurchaseOrderLine: builder.query<
      responses['getPurchaseOrderLine'],
      args['getPurchaseOrderLine']
    >({
      query: ({ params }) => ({
        url: `/procurement/purchase-order-lines/${params.id}/`,
        method: 'GET',
        validateStatus: (response, result) =>
          response.status === 200 && !result.isError,
      }),
      keepUnusedDataFor,
      providesTags: (_result, _error, _arg) =>
        _result
          ? [
              {
                type: 'Procurement',
                id: `purchase-order-line-${_arg.params.id}`,
              },
            ]
          : [],
    }),

    patchPurchaseOrderLine: builder.mutation<
      responses['patchPurchaseOrderLine'],
      args['patchPurchaseOrderLine']
    >({
      query: ({ body, params }) => ({
        url: `/procurement/purchase-order-lines/${params.id}/`,
        method: 'PATCH',
        body,
        validateStatus: (response, result) =>
          response.status === 200 && !result.isError,
      }),
      invalidatesTags: (_result, _error, _arg) =>
        _result
          ? [
              { type: 'Procurement', id: 'purchase-orders' },
              {
                type: 'Procurement',
                id: `purchase-order-${_result.po}`,
              },
              {
                type: 'Procurement',
                id: `purchase-order-line-${_arg.params.id}`,
              },
            ]
          : [],
    }),

    postPurchaseOrderLines: builder.mutation<
      responses['postPurchaseOrderLines'],
      args['postPurchaseOrderLines']
    >({
      query: ({ body, params }) => ({
        url: `/procurement/purchase-orders/${params.po_id}/lines/`,
        method: 'POST',
        body,
        // Backend returns 200 (all created), 207 (partial success), or 400
        // (none created). Treat 200 and 207 as success so the caller can
        // surface partial-success messages from result.errors.
        validateStatus: (response) =>
          response.status === 200 || response.status === 207,
      }),
      invalidatesTags: (_result, _error, _arg) =>
        _result
          ? [
              { type: 'Procurement', id: 'purchase-orders' },
              {
                type: 'Procurement',
                id: `purchase-order-${_arg.params.po_id}`,
              },
              {
                type: 'Procurement',
                id: `purchase-order-expected-receipts-${_arg.params.po_id}`,
              },
            ]
          : [],
    }),

    deletePurchaseOrderLine: builder.mutation<
      void,
      args['deletePurchaseOrderLine']
    >({
      query: ({ params }) => ({
        url: `/procurement/purchase-order-lines/${params.id}/`,
        method: 'DELETE',
        validateStatus: (response) => response.status === 204,
      }),
      // Refresh PO list + the per-PO detail. The deleted line lives inside
      // the PO's `lines` array which is fetched via getPurchaseOrder, so
      // invalidating the broad `purchase-orders` tag covers the open Edit
      // PO Lines screen.
      invalidatesTags: () => [{ type: 'Procurement', id: 'purchase-orders' }],
    }),

    getSupplierDetails: builder.query<
      responses['getSupplierDetails'],
      args['getSupplierDetails']
    >({
      query: ({ params }) => ({
        url: `/procurement/suppliers/${params.supplier_id}/details/`,
        method: 'GET',
        validateStatus: (response, result) =>
          response.status === 200 && !result.isError,
      }),
      providesTags: (_r, _e, arg) => [
        { type: 'Procurement', id: 'supplier-details' },
        {
          type: 'Procurement',
          id: `supplier-details-${arg.params.supplier_id}`,
        },
      ],
    }),

    putPurchaseOrderLine: builder.mutation<
      responses['putPurchaseOrderLine'],
      args['putPurchaseOrderLine']
    >({
      query: ({ body, params }) => ({
        url: `/procurement/purchase-order-lines/${params.id}/`,
        method: 'PUT',
        body,
        validateStatus: (response, result) =>
          response.status === 200 && !result.isError,
      }),
      invalidatesTags: (_result, _error, _arg) =>
        _result
          ? [
              {
                type: 'Procurement',
                id: `purchase-order-line-${_arg.params.id}`,
              },
            ]
          : [],
    }),
  }),
});

export const {
  // Suppliers
  usePostSuppliersMutation,
  useGetAllSuppliersQuery,
  useGetSupplierQuery,
  usePutSupplierMutation,
  usePatchSupplierMutation,
  useDeleteSupplierMutation,
  useGetSupplierScoreCardQuery,
  useGetSupplierCardsQuery,
  useGetSupplierDetailsQuery,

  // Supplier contacts
  usePostSupplierContactsMutation,
  useGetSupplierContactQuery,
  usePutSupplierContactMutation,
  usePatchSupplierContactMutation,
  useDeleteSupplierContactMutation,

  // Draft orders
  usePostDraftOrdersMutation,
  useGetAllDraftOrdersQuery,
  useGetDraftOrderQuery,
  usePatchDraftOrderMutation,
  usePutDraftOrderMutation,

  // Draft orders lines
  usePostDraftOrderLinesMutation,
  useGetDraftOrderLineQuery,
  usePatchDraftOrderLineMutation,
  usePutDraftOrderLineMutation,
  useDeleteDraftOrderLineMutation,

  // Purchase orders
  usePostDirectPurchaseOrderMutation,
  usePostPurchaseOrderMutation,
  useGetAllPurchaseOrdersQuery,
  useGetPurchaseOrderQuery,
  usePutPurchaseOrderMutation,
  usePatchPurchaseOrderMutation,
  usePostPurchaseOrderCancelMutation,
  usePostPurchaseOrderRestoreMutation,
  useDeletePurchaseOrderMutation,
  usePostPurchaseOrderStatusMutation,
  usePostPurchaseOrderReceiveMutation,
  useGetPurchaseOrderExpectedReceiptsQuery,
  useExportPurchaseOrderPdfMutation,
  useExportPurchaseOrderLogMutation,
  useGetPurchaseOrdersByPartQuery,
  usePostImportPreviewMutation,
  usePostImportCommitMutation,

  // Purchase order invoices
  useGetPurchaseOrderInvoicesAllQuery,
  useGetPurchaseOrderInvoiceQuery,
  usePostPurchaseOrderInvoiceMutation,
  usePatchPurchaseOrderInvoiceMutation,
  useDeletePurchaseOrderInvoiceMutation,

  // Purchase order lines
  useGetPurchaseOrderLineQuery,
  usePatchPurchaseOrderLineMutation,
  usePutPurchaseOrderLineMutation,
  usePostPurchaseOrderLinesMutation,
  useDeletePurchaseOrderLineMutation,

  //Dashboard
  useGetProcurementDashboardQuery,
} = procurementApi;

export namespace ProcurementApi {
  export const Api = procurementApi;
  export type Args = args;
  export type Responses = responses;
}
